import { cn } from "@/lib/utils";

type CardVariant = "default" | "elevated" | "outlined" | "interactive" | "status" | "glass";
type CardPadding = "none" | "sm" | "md" | "lg";

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: CardPadding;
  /** Status color for the left border when variant="status".
   * Accepts a domain status key (e.g. "active", "draft") or a raw CSS color string. */
  statusColor?: string;
  children: React.ReactNode;
}

const variantStyles: Record<CardVariant, string> = {
  default: "eco-glow",
  elevated: "eco-glow-lg",
  outlined: "border border-surface-dim",
  interactive: "eco-glow hover:eco-glow-lg transition-shadow cursor-pointer",
  status: "eco-glow border-l-4",
  glass: "glass-light",
};

const paddingStyles: Record<CardPadding, string> = {
  none: "",
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
};

/**
 * Status-to-border-color mapping for domain statuses
 *
 * Use with Card variant="status" to render a colored left border:
 * ```tsx
 * <Card variant="status" statusColor="active">...</Card>
 * <Card variant="status" statusColor={cardStatusColor[status] ?? "border-l-surface-dim"}>...</Card>
 * ```
 */
export const cardStatusColor: Record<string, string> = {
  draft: "border-l-surface-dim",
  preliminary: "border-l-amber-400",
  qualified: "border-l-blue-400",
  active: "border-l-green-500",
  reserved: "border-l-amber-400",
  sold: "border-l-surface-dim",
  expired: "border-l-red-400",
  withdrawn: "border-l-red-400",
  matched: "border-l-green-500",
  fulfilled: "border-l-surface-dim",
  in_progress: "border-l-blue-400",
  completed: "border-l-green-500",
  cancelled: "border-l-red-400",
  approved: "border-l-green-500",
  conditionally_approved: "border-l-amber-400",
  requirements_known: "border-l-blue-400",
  waiting: "border-l-surface-dim",
  no_contact: "border-l-surface-dim",
  rejected: "border-l-red-400",
};

/**
 * Card - Base card component with consistent styling
 *
 * Variants:
 * - default: Tonal lift with eco-glow shadow (white on surface-container)
 * - elevated: Stronger eco-glow for emphasis
 * - outlined: Subtle tonal border (surface-dim)
 * - interactive: Eco-glow with hover amplification for clickable cards
 * - status: Eco-glow card with a colored 4px left border indicating entity status
 * - glass: Glassmorphism container (55% white, 24px blur)
 *
 * Padding:
 * - none: No padding (for custom layouts)
 * - sm: Compact padding (12px)
 * - md: Standard padding (16px)
 * - lg: Generous padding (24px)
 *
 * Status color (only used with variant="status"):
 * - Pass a domain status key (e.g. "active", "draft") to use the standard color mapping
 * - Pass a Tailwind border class (e.g. "border-l-green-500") for custom colors
 * - Pass a raw CSS color string (e.g. "#22c55e") for arbitrary colors
 */
export function Card({
  variant = "default",
  padding = "md",
  statusColor,
  className,
  children,
  ...props
}: CardProps) {
  const isStatusVariant = variant === "status";

  // Resolve status color: domain key -> Tailwind class, or use as-is
  const statusBorderClass =
    isStatusVariant && statusColor
      ? (cardStatusColor[statusColor] ??
        (statusColor.startsWith("border-l-") ? statusColor : undefined))
      : undefined;

  // For raw CSS color strings (hex, rgb, etc.), apply via inline style
  const statusBorderStyle =
    isStatusVariant &&
    statusColor &&
    !cardStatusColor[statusColor] &&
    !statusColor.startsWith("border-l-")
      ? { borderLeftColor: statusColor }
      : undefined;

  return (
    <div
      {...props}
      className={cn(
        "rounded-xl bg-white",
        variantStyles[variant],
        statusBorderClass,
        paddingStyles[padding],
        className
      )}
      style={{ ...props.style, ...statusBorderStyle }}
    >
      {children}
    </div>
  );
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * CardHeader - Header section of a card
 * Use with Card for consistent card layouts
 */
export function CardHeader({ className, children, ...props }: CardHeaderProps) {
  return (
    <div className={cn("pb-4 mb-4", className)} {...props}>
      {children}
    </div>
  );
}

interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * CardBody - Main content section of a card
 * Use with Card for consistent card layouts
 */
export function CardBody({ className, children, ...props }: CardBodyProps) {
  return (
    <div className={cn("", className)} {...props}>
      {children}
    </div>
  );
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * CardFooter - Footer section of a card
 * Use with Card for consistent card layouts
 */
export function CardFooter({ className, children, ...props }: CardFooterProps) {
  return (
    <div className={cn("pt-4 mt-4", className)} {...props}>
      {children}
    </div>
  );
}
